import React from "react";
import Datatable from "app/partials/datatable/Datatable";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Box,
  IconButton,
  Grid,
  TableRow,
  TableCell,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import { AnanseApiService } from "app/services";
import { Button } from "../../components";

// Tipos
type ClientType = {
  id: string;
  name: string;
  email: string;
  subscriptions: SubscriptionType[];
};

type SubscriptionType = {
  id: string;
  billingType: "CREDIT_CARD" | "PIX" | string;
  cycle: "MONTHLY" | "YEARLY" | string;
  nextDueDate: Date | string;
  status: "ACTIVE" | "INACTIVE" | string;
  value: number;
};

// Componente SubscriptionDialog
const SubscriptionDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  subscription: ClientType | null;
  loading?: boolean;
}> = ({ open, onClose, subscription, loading = false }) => {
  const getStatusColor = (
    status: string,
  ): "success" | "warning" | "error" | "default" => {
    switch (status?.toLowerCase()) {
      case "active":
      case "ativo":
        return "success";
      case "pending":
      case "pendente":
        return "warning";
      case "cancelled":
      case "cancelado":
        return "error";
      case "inactive":
      case "inativo":
        return "error";
      default:
        return "default";
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDate = (dateString: Date | string): string => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatBillingType = (type: string): string => {
    const types: Record<string, string> = {
      CREDIT_CARD: "Cartão de Crédito",
      PIX: "PIX",
      BOLETO: "Boleto",
      DEBIT_CARD: "Cartão de Débito",
    };
    return types[type] || type;
  };

  const formatCycle = (cycle: string): string => {
    const cycles: Record<string, string> = {
      MONTHLY: "Mensal",
      YEARLY: "Anual",
      WEEKLY: "Semanal",
      BIWEEKLY: "Quinzenal",
    };
    return cycles[cycle] || cycle;
  };

  const formatStatus = (status: string): string => {
    const statuses: Record<string, string> = {
      ACTIVE: "Ativo",
      INACTIVE: "Inativo",
      PENDING: "Pendente",
      CANCELLED: "Cancelado",
      OVERDUE: "Vencido",
    };
    return statuses[status] || status;
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 300,
          }}
        >
          <Box textAlign="center">
            <CircularProgress size={48} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Carregando assinaturas...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 24,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 2,
          px: 3,
        }}
      >
        <Typography variant="h6" component="div">
          Detalhes da Assinatura
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, mt: 2 }}>
        {subscription && (
          <Box>
            {/* Informações do Cliente */}
            <Card
              variant="outlined"
              sx={{
                mb: 3,
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  color="text.primary"
                  fontWeight="bold"
                >
                  Informações do Cliente
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ minWidth: 30 }}
                      >
                        ID:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {subscription.id}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <PersonIcon
                        sx={{ mr: 1, color: "text.secondary", fontSize: 20 }}
                      />
                      <Typography variant="body1" fontWeight="medium">
                        {subscription.name}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <EmailIcon
                        sx={{ mr: 1, color: "text.secondary", fontSize: 20 }}
                      />
                      <Typography variant="body1">
                        {subscription.email}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: { xs: "flex-start", sm: "flex-end" },
                        height: "100%",
                      }}
                    >
                      <Chip
                        label={`${subscription.subscriptions?.length ||
                          0} assinatura(s)`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Lista de Assinaturas */}
            {subscription.subscriptions &&
            subscription.subscriptions.length > 0 ? (
              <>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  fontWeight="bold"
                  sx={{ mb: 2 }}
                >
                  Assinaturas
                </Typography>

                <Card variant="outlined">
                  <List sx={{ py: 0 }}>
                    {subscription.subscriptions.map((sub, index) => (
                      <Box key={sub.id}>
                        {index > 0 && <Divider />}
                        <ListItem
                          sx={{
                            py: 2,
                            px: 3,
                            "&:hover": {
                              bgcolor: "action.hover",
                            },
                          }}
                        >
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    gutterBottom
                                  >
                                    Tipo de Cobrança
                                  </Typography>
                                }
                                secondary={
                                  <Typography
                                    variant="body1"
                                    fontWeight="medium"
                                  >
                                    {formatBillingType(sub.billingType)}
                                  </Typography>
                                }
                              />
                            </Grid>

                            <Grid item xs={6} sm={2}>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    gutterBottom
                                  >
                                    Ciclo
                                  </Typography>
                                }
                                secondary={
                                  <Typography
                                    variant="body1"
                                    fontWeight="medium"
                                  >
                                    {formatCycle(sub.cycle)}
                                  </Typography>
                                }
                              />
                            </Grid>

                            <Grid item xs={6} sm={3}>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    gutterBottom
                                  >
                                    Próx. Vencimento
                                  </Typography>
                                }
                                secondary={
                                  <Typography
                                    variant="body1"
                                    fontWeight="medium"
                                  >
                                    {formatDate(sub.nextDueDate)}
                                  </Typography>
                                }
                              />
                            </Grid>

                            <Grid item xs={6} sm={2}>
                              <Chip
                                label={formatStatus(sub.status)}
                                color={getStatusColor(sub.status)}
                                size="small"
                                sx={{ fontWeight: "bold" }}
                              />
                            </Grid>

                            <Grid item xs={6} sm={2}>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    gutterBottom
                                  >
                                    Valor
                                  </Typography>
                                }
                                secondary={
                                  <Typography
                                    variant="body1"
                                    fontWeight="bold"
                                    color="text.primary"
                                  >
                                    {formatCurrency(sub.value)}
                                  </Typography>
                                }
                              />
                            </Grid>
                          </Grid>
                        </ListItem>
                      </Box>
                    ))}
                  </List>
                </Card>
              </>
            ) : (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  Nenhuma assinatura encontrada para este cliente.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Componente principal ListClients
const ListClients: React.FC = () => {
  const [clients, setClients] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [
    subscriptionCounts,
    setSubscriptionCounts,
  ] = React.useState<Record<string, number | null>>({});
  const loadingSubscriptionCounts = React.useRef<Set<string>>(new Set());
  const [
    showSubscriptions,
    setShowSubscriptions,
  ] = React.useState<ClientType | null>(null);

  const getCustomerEndpoint = (id: string): string =>
    `/asaaswebhook/test-customer/${id}`;

  React.useEffect(() => {
    const idsToLoad = clients
      .map(client => client.id)
      .filter(
        id =>
          id &&
          subscriptionCounts[id] === undefined &&
          !loadingSubscriptionCounts.current.has(id),
      );

    if (idsToLoad.length === 0) {
      return;
    }

    idsToLoad.forEach(id => loadingSubscriptionCounts.current.add(id));
    setSubscriptionCounts(currentCounts => {
      const nextCounts = { ...currentCounts };
      idsToLoad.forEach(id => {
        nextCounts[id] = null;
      });
      return nextCounts;
    });

    const service = new AnanseApiService();

    idsToLoad.forEach(async id => {
      try {
        const response = (await service.makeHttpRequest({
          url: getCustomerEndpoint(id),
          method: "GET",
        })) as ClientType;

        setSubscriptionCounts(currentCounts => ({
          ...currentCounts,
          [id]: response.subscriptions?.length || 0,
        }));
      } catch (error) {
        console.error("Erro ao buscar quantidade de assinaturas:", error);
        setSubscriptionCounts(currentCounts => ({
          ...currentCounts,
          [id]: 0,
        }));
      } finally {
        loadingSubscriptionCounts.current.delete(id);
      }
    });
  }, [clients, subscriptionCounts]);

  const getSubscriptions = async (id: string): Promise<void> => {
    const endPoint = getCustomerEndpoint(id);
    const service = new AnanseApiService();

    // Abre o dialog com loading
    setShowSubscriptions({
      id,
      name: "",
      email: "",
      subscriptions: [],
    } as ClientType);
    setLoading(true);

    try {
      const response = (await service.makeHttpRequest({
        url: endPoint,
        method: "GET",
      })) as ClientType;

      setShowSubscriptions(response);
      setSubscriptionCounts(currentCounts => ({
        ...currentCounts,
        [id]: response.subscriptions?.length || 0,
      }));
    } catch (error) {
      console.error("Erro ao buscar assinaturas:", error);
      setShowSubscriptions(null);
      // Aqui você pode adicionar uma notificação de erro
    } finally {
      setLoading(false);
    }
  };

  const headRows = [
    {
      key: 1,
      column: "ID",
      label: "ID",
    },
    {
      key: 2,
      column: "Name",
      label: "Nome",
    },
    {
      key: 3,
      column: "Email",
      label: "E-mail",
    },
    {
      key: 4,
      column: "Assinaturas",
      label: "Assinaturas",
    },
    {
      key: 5,
      column: "Ações",
      label: "Ações",
    },
  ];

  const formatRow = (row: any) => {
    return (
      <TableRow key={row.id}>
        <TableCell scope="row">{row.id}</TableCell>
        <TableCell>{row.name}</TableCell>
        <TableCell>{row.email}</TableCell>
        <TableCell>
          {subscriptionCounts[row.id] === null ? (
            <CircularProgress size={18} />
          ) : (
            `${subscriptionCounts[row.id] || 0} assinatura(s)`
          )}
        </TableCell>
        <TableCell>
          <Button
            onClick={() => getSubscriptions(row.id)}
            // variant="contained"
            // size="small"
          >
            Ver
          </Button>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div>
      <SubscriptionDialog
        open={showSubscriptions !== null}
        onClose={() => setShowSubscriptions(null)}
        subscription={showSubscriptions}
        loading={loading}
      />

      <Datatable
        title="Clientes Asaas"
        endpoint="/asaaswebhook/test-customers"
        headRows={headRows}
        formatRow={formatRow}
        placeholderSearch="Buscar..."
        reload={false}
        rows={clients}
        setRows={setClients}
      />
    </div>
  );
};

export default ListClients;
